import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../../services/api';
import { Shield, Check, X, Clock, Camera, Instagram, ExternalLink, Eye, AlertTriangle, Trash2, Bell } from 'lucide-react';
import { PageTooltip } from '../components/TooltipSystem';

const TYPE_CONFIG = {
  review_screenshot: { label: 'Screenshot Recensione', icon: Camera, color: '#4285F4' },
  instagram_tag: { label: 'Tag Instagram', icon: Instagram, color: '#E1306C' },
};

const STATUS_CONFIG = {
  pending_review: { label: 'In attesa', color: '#F59E0B', bg: '#FFFBEB' },
  approved: { label: 'Approvato', color: '#10B981', bg: '#ECFDF5' },
  rejected: { label: 'Rifiutato', color: '#EF4444', bg: '#FEF2F2' },
};

export default function AdminSocialProofs() {
  const [proofs, setProofs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending_review');
  const [processing, setProcessing] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [adminNotifs, setAdminNotifs] = useState([]);
  const [notifsLoading, setNotifsLoading] = useState(true);

  const fetchProofs = (status) => {
    setLoading(true);
    adminApi.get(`/admin/social-proofs${status ? `?status=${status}` : ''}`)
      .then(r => setProofs(r.data.proofs || []))
      .catch(() => toast.error('Errore caricamento'))
      .finally(() => setLoading(false));
  };

  const fetchAdminNotifs = () => {
    setNotifsLoading(true);
    adminApi.get('/admin/notifications', { params: { limit: 30 } })
      .then(r => setAdminNotifs(r.data.items || []))
      .catch(() => {})
      .finally(() => setNotifsLoading(false));
  };

  useEffect(() => { fetchProofs(filter); }, [filter]);
  useEffect(() => { fetchAdminNotifs(); }, []);

  const handleReview = async (id, action) => {
    setProcessing(id);
    try {
      await adminApi.put(`/admin/social-proofs/${id}/review`, { action });
      toast.success(action === 'approve' ? 'Prova approvata, punti assegnati!' : 'Prova rifiutata');
      fetchProofs(filter);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally {
      setProcessing(null);
    }
  };

  const markNotifRead = async (id) => {
    try {
      await adminApi.post(`/admin/notifications/${id}/read`);
      setAdminNotifs(prev => prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
    } catch {}
  };

  const dismissNotif = async (id) => {
    try {
      await adminApi.delete(`/admin/notifications/${id}`);
      setAdminNotifs(prev => prev.filter(n => n.id !== id));
    } catch {
      toast.error('Errore');
    }
  };

  const markAllRead = async () => {
    try {
      await adminApi.post('/admin/notifications/mark-all-read');
      setAdminNotifs(prev => prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      toast.success('Tutte le notifiche segnate come lette');
    } catch {
      toast.error('Errore');
    }
  };

  const unreadNotifs = adminNotifs.filter(n => !n.read_at);

  // Notif kind filter (staff_void, future kinds...)
  const [notifKindFilter, setNotifKindFilter] = useState('all');
  const NOTIF_KINDS = [
    { id: 'all', label: 'Tutti' },
    { id: 'unread', label: 'Da leggere' },
    { id: 'staff_void', label: 'Annullamenti' },
  ];
  const filteredNotifs = adminNotifs.filter(n => {
    if (notifKindFilter === 'all') return true;
    if (notifKindFilter === 'unread') return !n.read_at;
    return n.kind === notifKindFilter;
  });
  const kindCounts = adminNotifs.reduce((acc, n) => {
    acc[n.kind] = (acc[n.kind] || 0) + 1;
    return acc;
  }, {});

  const pending = proofs.filter(p => p.status === 'pending_review');
  const resolved = proofs.filter(p => p.status !== 'pending_review');

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-5xl" data-testid="admin-social-proofs-page">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl tracking-tight font-bold text-[#1A1A1A] flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#E1306C] to-[#4285F4] flex items-center justify-center">
            <Shield size={20} className="text-white" />
          </div>
          Notifiche
        </h1>
        <p className="text-[#64748B] text-sm mt-1">Verifica se hai qualche attività in sospeso e tieni tutto sotto controllo</p>
      </div>

      {/* Admin notifications inbox (staff voids, system events, ...) */}
      <div className="mb-6 bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="admin-notifs-section">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-amber-50 flex items-center justify-center">
              <Bell size={15} className="text-amber-600" />
            </div>
            <div>
              <h2 className="font-semibold text-[#1A1A1A] text-sm">Errori operativi staff & avvisi</h2>
              <p className="text-[10px] text-[#94A3B8]">Annullamenti transazioni, eventi sistema</p>
            </div>
          </div>
          {unreadNotifs.length > 0 && (
            <button onClick={markAllRead} data-testid="notifs-mark-all-read"
              className="text-[11px] font-medium text-[#D44E20] hover:underline">
              Segna tutte come lette ({unreadNotifs.length})
            </button>
          )}
        </div>

        {/* Kind filter chips */}
        {adminNotifs.length > 0 && (
          <div className="flex gap-1.5 mb-3 flex-wrap" data-testid="notif-kind-filters">
            {NOTIF_KINDS.map(k => {
              const count = k.id === 'all' ? adminNotifs.length
                : k.id === 'unread' ? unreadNotifs.length
                : (kindCounts[k.id] || 0);
              return (
                <button key={k.id}
                  onClick={() => setNotifKindFilter(k.id)}
                  data-testid={`notif-kind-${k.id}`}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-colors ${
                    notifKindFilter === k.id
                      ? 'bg-amber-100 text-amber-700 border border-amber-300'
                      : 'bg-slate-50 text-[#64748B] border border-slate-200 hover:bg-slate-100'
                  }`}>
                  {k.label} <span className="opacity-70 ml-0.5">({count})</span>
                </button>
              );
            })}
          </div>
        )}

        {notifsLoading ? (
          <p className="text-xs text-[#94A3B8] text-center py-4">Caricamento…</p>
        ) : filteredNotifs.length === 0 ? (
          <p className="text-xs text-[#94A3B8] text-center py-4">
            {adminNotifs.length === 0
              ? 'Nessuna attività anomala recente. Tutto regolare!'
              : 'Nessuna notifica per questo filtro.'}
          </p>
        ) : (
          <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
            {filteredNotifs.map(n => {
              const isVoid = n.kind === 'staff_void';
              const Icon = isVoid ? AlertTriangle : Bell;
              const accent = n.severity === 'warning' ? '#F59E0B' : n.severity === 'error' ? '#EF4444' : '#3B82F6';
              const created = n.created_at ? new Date(n.created_at).toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
              return (
                <div key={n.id} data-testid={`admin-notif-${n.id}`}
                  className={`flex items-start gap-3 px-3 py-2.5 rounded-xl border ${n.read_at ? 'border-slate-100 bg-white' : 'border-amber-200 bg-amber-50/40'}`}>
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${accent}15` }}>
                    <Icon size={13} style={{ color: accent }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[#1A1A1A]">{n.title}</p>
                    <p className="text-[11px] text-[#64748B] line-clamp-2 mt-0.5">{n.body}</p>
                    <p className="text-[10px] text-[#94A3B8] mt-1">{created}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!n.read_at && (
                      <button onClick={() => markNotifRead(n.id)} data-testid={`notif-read-${n.id}`}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-[#10B981] hover:bg-[#ECFDF5]" title="Segna come letta">
                        <Check size={13} />
                      </button>
                    )}
                    <button onClick={() => dismissNotif(n.id)} data-testid={`notif-dismiss-${n.id}`}
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-[#94A3B8] hover:bg-slate-100 hover:text-[#EF4444]" title="Elimina">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-6" data-testid="sp-filter-tabs">
        {[
          { id: 'pending_review', label: 'In attesa', count: pending.length },
          { id: '', label: 'Tutti' },
          { id: 'approved', label: 'Approvati' },
          { id: 'rejected', label: 'Rifiutati' },
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`sp-filter-${f.id || 'all'}`}
            className={`px-3 py-2 rounded-xl text-xs font-medium transition-all ${
              filter === f.id
                ? 'bg-[#D44E20] text-white'
                : 'text-[#64748B] hover:bg-slate-100'
            }`}
          >
            {f.label} {f.count !== undefined ? `(${f.count})` : ''}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : proofs.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          <Shield size={40} className="text-slate-200 mx-auto mb-3" />
          <p className="text-[#1A1A1A] font-medium">Nessuna prova {filter === 'pending_review' ? 'in attesa' : ''}</p>
          <p className="text-sm text-[#64748B] mt-1">Le prove caricate dai clienti appariranno qui.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {proofs.map(proof => {
            const typeConf = TYPE_CONFIG[proof.type] || TYPE_CONFIG.review_screenshot;
            const statusConf = STATUS_CONFIG[proof.status] || STATUS_CONFIG.pending_review;
            const TypeIcon = typeConf.icon;
            return (
              <div
                key={proof.id}
                className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-shadow"
                data-testid={`sp-card-${proof.id}`}
              >
                <div className="flex items-start gap-4">
                  <PageTooltip page="social-proofs" />
                  {/* Image preview */}
                  {proof.image_url && (
                    <button
                      onClick={() => setPreviewUrl(proof.image_url)}
                      className="w-16 h-16 rounded-xl overflow-hidden border border-slate-100 flex-shrink-0 hover:opacity-80 transition-opacity relative group"
                    >
                      <img src={proof.image_url} alt="Screenshot" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <Eye size={16} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </button>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <TypeIcon size={14} style={{ color: typeConf.color }} />
                      <span className="text-sm font-medium text-[#1A1A1A]">{typeConf.label}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ color: statusConf.color, backgroundColor: statusConf.bg }}>
                        {statusConf.label}
                      </span>
                    </div>
                    <p className="text-sm text-[#1A1A1A] font-semibold">{proof.customer_name}</p>
                    <p className="text-xs text-[#64748B]">
                      {proof.platform} &middot; {proof.created_at ? new Date(proof.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                    </p>
                    {proof.ai_result?.reason && (
                      <p className="text-[10px] text-[#94A3B8] mt-1 italic">
                        AI: {proof.ai_result.reason} (confidenza: {Math.round((proof.ai_result.confidence || 0) * 100)}%)
                      </p>
                    )}
                    {proof.points_awarded > 0 && (
                      <p className="text-xs font-medium text-[#10B981] mt-1">+{proof.points_awarded} punti assegnati</p>
                    )}
                  </div>

                  {/* Actions */}
                  {proof.status === 'pending_review' && (
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleReview(proof.id, 'approve')}
                        disabled={processing === proof.id}
                        data-testid={`sp-approve-${proof.id}`}
                        className="w-9 h-9 flex items-center justify-center rounded-xl bg-[#ECFDF5] text-[#10B981] hover:bg-[#10B981] hover:text-white transition-colors disabled:opacity-50"
                      >
                        <Check size={16} />
                      </button>
                      <button
                        onClick={() => handleReview(proof.id, 'reject')}
                        disabled={processing === proof.id}
                        data-testid={`sp-reject-${proof.id}`}
                        className="w-9 h-9 flex items-center justify-center rounded-xl bg-[#FEF2F2] text-[#EF4444] hover:bg-[#EF4444] hover:text-white transition-colors disabled:opacity-50"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Image preview modal */}
      {previewUrl && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setPreviewUrl(null)}>
          <div className="relative max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setPreviewUrl(null)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center text-[#64748B] hover:text-[#1A1A1A]"
            >
              <X size={16} />
            </button>
            <img src={previewUrl} alt="Preview" className="w-full rounded-2xl shadow-2xl" />
          </div>
        </div>
      )}
    </div>
  );
}
